/**
 * Server-only env-var validation for Supabase.
 *
 * All three vars are read on the server (Server Components, Route
 * Handlers, Server Actions, the proxy). None are exposed to the browser
 * — there is no browser-side Supabase client in this codebase, so no
 * `NEXT_PUBLIC_*` prefix is needed.
 *
 * If you ever add browser-side Supabase calls, you'll need to introduce
 * `NEXT_PUBLIC_SUPABASE_URL` + `NEXT_PUBLIC_SUPABASE_ANON_KEY` and have
 * the browser client read those instead.
 */

import { z } from "zod";

const PublicSchema = z.object({
  SUPABASE_URL: z.string().url(),
  SUPABASE_ANON_KEY: z.string().min(20),
});

const ServerSchema = PublicSchema.extend({
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(20),
});

export function readPublicEnv(): z.infer<typeof PublicSchema> {
  const parsed = PublicSchema.safeParse({
    SUPABASE_URL: process.env.SUPABASE_URL,
    SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
  });
  if (!parsed.success) {
    throw new Error(
      `Missing Supabase env vars: ${parsed.error.issues
        .map((i) => i.path.join("."))
        .join(", ")}. Set them in .env.local (dev) or in your Vercel project settings (prod).`,
    );
  }
  return parsed.data;
}

export function readServerEnv(): z.infer<typeof ServerSchema> {
  const parsed = ServerSchema.safeParse({
    SUPABASE_URL: process.env.SUPABASE_URL,
    SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
    SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY,
  });
  if (!parsed.success) {
    throw new Error(
      `Missing Supabase server env vars: ${parsed.error.issues
        .map((i) => i.path.join("."))
        .join(", ")}. Service role key is required for the Stripe webhook and waitlist writes.`,
    );
  }
  return parsed.data;
}

/**
 * Returns true iff the public Supabase env vars are set. Useful in code
 * paths that should silently no-op when Supabase isn't configured (e.g.
 * the proxy on a fresh Vercel preview before env vars are wired up).
 */
export function hasPublicEnv(): boolean {
  return Boolean(process.env.SUPABASE_URL && process.env.SUPABASE_ANON_KEY);
}
